import { getAuth } from "firebase/auth";
import { USERS_API_BASE_URL } from "../config/api";

// Organisations and their team (Trello #554 / #562), served by app-backend
// /api/org (proxied to users-api /org). Roles are "editor" (manages users, areas
// and monitoring) and "viewer" (dashboards only). Telcofy staff are never members.

export type OrgRole = "editor" | "viewer";

export interface Organisation {
  id: string;
  name: string | null;
  planName: string | null;
  usersIncluded: number;
  usersInUse: number;
}

export interface OrgInfo {
  organisation: Organisation | null; // null: the user isn't in an organisation
  me?: { uid: string; role: OrgRole };
}

export interface OrgMember {
  uid: string;
  email: string;
  displayName: string | null;
  role: OrgRole;
  status: "active" | "invited"; // invited = never signed in
  lastSignInAt: string | null;
  /** Last session renewal while using the app (Firebase lastRefreshTime); newer backends only. */
  lastActiveAt?: string | null;
  invitedAt: string | null;
  isYou: boolean;
}

export interface MembersResult {
  members: OrgMember[];
  usersIncluded: number;
  usersInUse: number;
}

/** A refusal from the backend, with its code (e.g. "seat_limit_reached"). */
export class OrgApiError extends Error {
  constructor(public code: string, public status: number, public data: Record<string, unknown> = {}) {
    super(code);
  }
}

// An editor's invite holds a place for this long (users-api INVITE_TTL_DAYS).
export const INVITE_TTL_DAYS = 7;

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const user = getAuth().currentUser;
  if (!user) throw new OrgApiError("not_signed_in", 401);
  const res = await fetch(`${USERS_API_BASE_URL}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await user.getIdToken()}` },
  });
  const body = res.status === 204 ? {} : await res.json().catch(() => ({}));
  if (!res.ok) throw new OrgApiError(body?.error || `http_${res.status}`, res.status, body);
  return body as T;
}

export const orgApi = {
  /**
   * The signed-in user's organisation and role. Resolves to no organisation when
   * the backend has no organisations yet (a 404 from an older backend), so the
   * app simply hides the team features.
   */
  async getOrg(): Promise<OrgInfo> {
    try {
      return await call<OrgInfo>("/org");
    } catch (err) {
      if (err instanceof OrgApiError && err.status === 404) return { organisation: null };
      throw err;
    }
  },
  listMembers: () => call<MembersResult>("/org/members"),
  invite: (email: string, role: OrgRole) =>
    call<{
      uid: string;
      status: string;
      emailSent: boolean;
      emailKind?: "invite" | "access_restored" | "access_added";
    }>("/org/members", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),
  changeRole: (uid: string, role: OrgRole) =>
    call<unknown>(`/org/members/${encodeURIComponent(uid)}`, { method: "PATCH", body: JSON.stringify({ role }) }),
  remove: (uid: string) =>
    call<{ uid: string; status: string; emailSent?: boolean }>(`/org/members/${encodeURIComponent(uid)}`, { method: "DELETE" }),
  resendInvite: (uid: string) =>
    call<unknown>(`/org/members/${encodeURIComponent(uid)}/resend-invite`, { method: "POST" }),
};

/** Plain words for each refusal the backend can send. */
export function orgErrorMessage(err: unknown): string {
  const code = err instanceof OrgApiError ? err.code : "";
  switch (code) {
    case "seat_limit_reached":
      return "All the users on your plan are in use.";
    case "last_editor":
      return "Your organisation needs at least one editor. Make someone else an editor first.";
    case "already_in_team":
      return "That person is already in your team.";
    case "member_of_other_organisation":
      return "That email address belongs to another organisation's account. Ask them to use a different address.";
    case "editor_role_required":
      return "Only editors can manage the team.";
    case "already_signed_in":
      return "This person has already signed in, so there's no invite to resend.";
    case "resend_limit_reached":
      return "Too many invites sent to this person just now. Try again in an hour.";
    case "invalid_email":
      return "Enter a valid email address.";
    case "not_found":
      return "That user is no longer in your team. Refresh to see the current list.";
    default:
      return "Something went wrong. Please try again.";
  }
}
